// frontend/src/app/components/FirstTimeWelcomeModal.tsx
import React from "react";
import { Link } from "react-router-dom";

export default function FirstTimeWelcomeModal(props: {
  open: boolean;
  onClose: () => void;
  tenantUuid?: string;
  roles?: string[];
}) {
  const { open, onClose, tenantUuid, roles } = props;
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome"
    >
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
        type="button"
      />

      {/* Modal */}
      <div className="relative w-[min(720px,92vw)] rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-semibold text-zinc-900">Welcome 👋</div>
            <div className="mt-1 text-sm text-zinc-600">
              Your tenant is ready. Follow this checklist to go live fast and get clean tenant-scoped signals in
              dashboards.
            </div>
            <div className="mt-2 text-xs text-zinc-500">
              Tenant:{" "}
              <code className="rounded bg-zinc-100 px-1 py-0.5">
                {tenantUuid || "unknown"}
              </code>{" "}
              · Roles:{" "}
              <code className="rounded bg-zinc-100 px-1 py-0.5">
                {(roles || []).join(", ") || "unknown"}
              </code>
            </div>
          </div>

          <button
            className="rounded-lg border border-zinc-200 px-3 py-1.5 text-sm hover:bg-zinc-50"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </div>

        <div className="mt-5 grid gap-3">
          <div className="rounded-xl border border-zinc-200 p-4">
            <div className="text-sm font-semibold text-zinc-900">
              Getting Started Checklist
            </div>

            <ul className="mt-3 space-y-3 text-sm text-zinc-700">
              <li className="flex items-start gap-2">
                <span className="mt-[2px] inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-xs">
                  1
                </span>
                <span>
                  <b>Set up Providers</b>
                  <div className="mt-0.5 text-xs text-zinc-600">
                    Connect at least one LLM provider so workflows can run end-to-end.{" "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/providers"
                      onClick={onClose}
                    >
                      → Providers
                    </Link>
                  </div>
                </span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-[2px] inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-xs">
                  2
                </span>
                <span>
                  <b>Use Starter Apps to launch fast</b>
                  <div className="mt-0.5 text-xs text-zinc-600">
                    Clone a starter app into your tenant and run a quick test.{" "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/ingress/starter-apps"
                      onClick={onClose}
                    >
                      → Starter Apps
                    </Link>
                    {" · "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/ingress/requests"
                      onClick={onClose}
                    >
                      Run a test request
                    </Link>
                  </div>
                </span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-[2px] inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-xs">
                  3
                </span>
                <span>
                  <b>Create or verify an Ingress App</b>
                  <div className="mt-0.5 text-xs text-zinc-600">
                    Create a new app (or confirm one exists) and attach it to a flow and schema.{" "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/ingress/apps"
                      onClick={onClose}
                    >
                      → Ingress Apps
                    </Link>
                  </div>
                </span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-[2px] inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-xs">
                  4
                </span>
                <span>
                  <b>Publish the App</b>
                  <div className="mt-0.5 text-xs text-zinc-600">
                    Publish when it’s ready for end users (auth mode, schema, and limits finalized).{" "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/ingress/apps"
                      onClick={onClose}
                    >
                      → Manage publishing
                    </Link>
                  </div>
                </span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-[2px] inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-xs">
                  5
                </span>
                <span>
                  <b>Share the end-user experience</b>
                  <div className="mt-0.5 text-xs text-zinc-600">
                    Send customers or end users the Ingress App page and Chat Runner link to start using the app.{" "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/ingress/apps"
                      onClick={onClose}
                    >
                      → Ingress Apps
                    </Link>
                    {" · "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/ingress"
                      onClick={onClose}
                    >
                      Open Chat Runner
                    </Link>
                  </div>
                </span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-[2px] inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-xs">
                  6
                </span>
                <span>
                  <b>Monitor in Intelligence</b>
                  <div className="mt-0.5 text-xs text-zinc-600">
                    Track requests, cost, tokens, provider health, and tenant-scoped metrics in Intelligence.{" "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/overview"
                      onClick={onClose}
                    >
                      → Overview
                    </Link>
                    {" · "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/observability/platform"
                      onClick={onClose}
                    >
                      Platform Health
                    </Link>
                    {" · "}
                    <Link
                      className="text-blue-700 hover:underline"
                      to="/admin/observability/providers"
                      onClick={onClose}
                    >
                      Provider Health
                    </Link>
                  </div>
                </span>
              </li>
            </ul>

            <div className="mt-4 text-xs text-zinc-500">
              Tip: This checklist appears only on your first login and is cleared when you close this window.
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              className="rounded-xl border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50"
              onClick={onClose}
              type="button"
            >
              Not now
            </button>

            <Link
              to="/admin/providers"
              className="rounded-xl bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black"
              onClick={onClose}
            >
              Set up Providers →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}